import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw, Share2, Check } from "lucide-react";
import { ROLES } from "../../game/roles";
import { ROLE_COLORS, ROLE_ICONS, ROLE_INK } from "./roleIcons";
import { useResultShare } from "./useResultShare";
import { useResultStats } from "./useResultStats";
import { DESTINATION_QUOTES } from "../../game/destinationQuotes";
import { analyzeCrew } from "../../game/analysis";
import { ChallengeOutcome, IslandHistory, PersonalRecord, ReachedStat } from "./ResultExtras";
import { LeaderboardPanel } from "./LeaderboardPanel";
import { challengeOutcome } from "../../lib/challenge";
import { RESULT_ENTER, TAP } from "./motionPresets";
import { SHARE_DOMAIN_LABEL } from "../../config/lumacofono";

const CrewList = ({ assignments }) => (
  <ul className="mt-2 space-y-1">
    {ROLES.map((r) => (
      <li
        key={r.key}
        className="flex items-center justify-between gap-3 rounded-full bg-white pl-3 pr-3.5 py-[6px]"
        data-testid={`result-role-${r.key}`}
      >
        <span className="flex items-center gap-2 shrink-0">
          <span className="w-2 h-2 rounded-full" style={{ background: ROLE_COLORS[r.key] }} />
          <span className="label-mono text-[9px] text-[#506780]">{r.label}</span>
        </span>
        <span className="text-[13px] font-bold text-[#14233D] text-right truncate">{assignments[r.key]?.name}</span>
      </li>
    ))}
  </ul>
);

const IslandBox = ({ island, isLaughTale, quote }) => (
  <div className="island-box mt-5 rounded-[26px] bg-[#EAF7FF] px-5 pt-4 pb-5 text-center">
    <p className="label-mono text-[#147E9D]">Sei arrivato fino a:</p>
    {isLaughTale && (
      <p className="label-mono text-[9px] text-[#9A6A00] mt-2" data-testid="result-laugh-tale-note">
        Hai raggiunto la fine del viaggio
      </p>
    )}
    <p
      data-testid="result-island"
      className={`font-display leading-[1] mt-2 break-words ${
        isLaughTale ? "text-[3.1rem] text-laugh laugh-glow" : "text-[2.7rem] text-pop"
      }`}
    >
      {island.toUpperCase()}
    </p>
    {quote && (
      <p className="text-[13px] font-semibold text-[#14233D] mt-3 leading-snug" data-testid="result-quote">
        “{quote}”
      </p>
    )}
    <p
      className="inline-block font-display text-sm tracking-[0.12em] text-[#14233D] bg-[#FFD166] rounded-full px-4 py-1.5 mt-4"
      data-testid="result-challenge"
    >
      RIESCI A BATTERMI?
    </p>
  </div>
);

export const ResultCard = ({ assignments, island, isLaughTale, crewName, quote }) => (
  <div className={`result-card rounded-[32px] overflow-hidden ${isLaughTale ? "result-card-lt" : ""}`} data-testid="result-card">
    <div className="result-band px-5 py-3 flex items-center justify-between">
      <p className="label-mono text-white">Costruisci la tua ciurma</p>
      <span className="flex gap-1">
        <span className="w-2.5 h-2.5 rounded-full bg-white/90" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FFD166]" />
      </span>
    </div>
    <div className="px-5 pt-4 pb-5">
      {crewName && (
        <p className="font-display text-[1.6rem] leading-[1.05] text-pop-dark break-words mb-1" data-testid="result-crew-name">
          {crewName.toUpperCase()}
        </p>
      )}
      <h2 className={`font-display text-[#14233D] ${crewName ? "text-base" : "text-2xl"}`}>LA TUA CIURMA</h2>
      <CrewList assignments={assignments} />
      <IslandBox island={island} isLaughTale={isLaughTale} quote={quote} />
      <p className="text-center text-[11px] font-semibold text-[#506780] mt-3 tracking-wide" data-testid="result-site">
        {SHARE_DOMAIN_LABEL}
      </p>
    </div>
  </div>
);

const SHARE_MESSAGES = {
  image: "Immagine pronta! Salvala o condividila nelle tue Stories.",
  text: "Risultato copiato negli appunti: incollalo dove vuoi!",
};

const ResultActions = ({ onReplay, onShare, status }) => (
  <>
    <motion.button
      type="button"
      data-testid="play-again-btn"
      onClick={onReplay}
      whileTap={TAP}
      className="spin-btn spin-ready w-full rounded-full font-display text-xl tracking-[0.12em] text-white py-4 mt-9 flex items-center justify-center gap-3"
    >
      <RotateCcw size={20} /> GIOCA ANCORA
    </motion.button>
    <motion.button
      type="button"
      data-testid="share-result-btn"
      onClick={onShare}
      whileTap={TAP}
      className="share-btn w-full rounded-full font-display text-base tracking-[0.12em] text-[#7433C1] py-4 mt-4 flex items-center justify-center gap-3"
    >
      {status ? <Check size={18} className="text-[#9A6A00]" /> : <Share2 size={18} />}
      CONDIVIDI RISULTATO
    </motion.button>
    {status && (
      <p className="text-center text-xs text-[#14233D]/85 mt-3" data-testid="share-copied-msg">
        {SHARE_MESSAGES[status]}
      </p>
    )}
  </>
);

const CrewNameField = ({ value, onChange }) => (
  <label className="block mb-5" data-testid="crew-name-field">
    <span className="label-mono text-[#7433C1]">Dai un nome alla tua ciurma (facoltativo)</span>
    <input
      type="text"
      value={value}
      maxLength={32}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Es. Pirati Lumacofono"
      data-testid="crew-name-input"
      className="mt-2 w-full rounded-full bg-white border-2 border-[#D0E4F3] focus:border-[#A989FF] outline-none px-5 py-3 text-base font-semibold text-[#14233D] placeholder:text-[#5E5E8A]"
    />
  </label>
);

const CrewAnalysis = ({ assignments }) => (
  <section className="mt-8 rounded-[26px] bg-white border border-[#D0E4F3] px-5 py-4" data-testid="crew-analysis">
    <p className="label-mono text-[#147E9D] mb-3">Analisi della ciurma</p>
    <ul className="space-y-2">
      {analyzeCrew(assignments).map((a) => {
        const Icon = ROLE_ICONS[a.key === "command" ? "captain" : a.key];
        return (
          <li key={a.key} className="flex items-center justify-between gap-3" data-testid={`analysis-${a.key}`}>
            <span className="flex items-center gap-2 text-sm font-semibold text-[#14233D]">
              <Icon size={15} style={{ color: ROLE_INK[a.key === "command" ? "captain" : a.key] }} />
              {a.label}
            </span>
            <span className={`analysis-pill tier-${a.tier} font-display text-sm rounded-full px-3 py-0.5`}>{a.verdict}</span>
          </li>
        );
      })}
    </ul>
  </section>
);

export const ResultScreen = ({ assignments, island, isLaughTale, destinationIndex, challenge, boardCode, onBoardCreated, onReplay }) => {
  const [crewName, setCrewName] = useState("");
  const name = crewName.trim();
  const reachedPercent = useResultStats(destinationIndex);
  const shareData = useMemo(
    () => ({
      assignments, island, isLaughTale, crewName: name, quote: DESTINATION_QUOTES[island],
      challenge: challenge ? { outcome: challengeOutcome(challenge, destinationIndex), island: challenge.island } : null,
    }),
    [assignments, island, isLaughTale, name, challenge, destinationIndex],
  );
  const { quote } = shareData;
  const { status, share } = useResultShare(shareData, destinationIndex);
  const headline = isLaughTale ? "HAI RAGGIUNTO LAUGH TALE" : `LA TUA CIURMA SI FERMA A ${island.toUpperCase()}`;

  return (
    <motion.main
      {...RESULT_ENTER}
      className="max-w-md mx-auto px-5 pt-7 pb-12 min-h-[100dvh] relative z-10"
      data-testid="result-screen"
    >
      <h1
        data-testid="journey-result-message"
        className={`font-display text-[1.7rem] leading-[1.05] mb-6 ${isLaughTale ? "text-laugh" : "text-[#14233D]"}`}
      >
        {headline}
      </h1>
      {challenge && <ChallengeOutcome challenge={challenge} destinationIndex={destinationIndex} />}
      <CrewNameField value={crewName} onChange={setCrewName} />
      <ResultCard assignments={assignments} island={island} isLaughTale={isLaughTale} crewName={name} quote={quote} />
      <PersonalRecord destinationIndex={destinationIndex} />
      <IslandHistory destinationIndex={destinationIndex} />
      {reachedPercent !== null && <ReachedStat percent={reachedPercent} island={island} />}
      <CrewAnalysis assignments={assignments} />
      <ResultActions onReplay={onReplay} onShare={share} status={status} />
      <LeaderboardPanel
        boardCode={boardCode}
        destinationIndex={destinationIndex}
        crewName={name}
        onBoardCreated={onBoardCreated}
      />
    </motion.main>
  );
};
